import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { PacksDataService, PacksColumnsService } from '../../services';
import { DialogPackFormComponent } from './pack-form/pack-form.component';
import { DialogPackDetailsComponent } from './pack-details/pack-details.component';
import { Pack, PacksResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-packs',
  templateUrl: './packs.component.html',
  styleUrls: ['./packs.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class PacksComponent implements OnInit, OnDestroy {

  private packAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private packEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private packDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public packToDelete: Pack = {};
  public packs: Pack[] = [];
  public pack: Pack = {};
  public selectedPacks: Pack[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private packsDataService: PacksDataService,
    private packsColumnsService: PacksColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('PacksComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Packs - Management');
    this.primengConfig.ripple = true;
    this.getPacks();
  }

  ngOnDestroy(): void {
    console.log('PacksComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getPacks(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.packsDataService.getPacks(this.parameters).subscribe((response: PacksResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.packs = response.results;
        } else {
          this.packs = this.packs.concat(response.results);
        }
        if (response.inlineCount > this.packs.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getPacks();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PACKSNOTLOADED')});
    });
  }

  importPacks(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.packsDataService.addPacks(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('PACKSCREATED')});
                if (proceed === j) {
                  this.getPacks();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PACKSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PACKSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.packsDataService.updatePacks(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('PACKSUPDATED')});
                if (proceed === j) {
                  this.getPacks();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PACKSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PACKSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.packsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('packs.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedPacks() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Packs?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.packsDataService.deletePacks(this.selectedPacks).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Packs Deleted', life: 3000});
            this.getPacks();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Packs not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Packs not deleted'});
        });
        this.selectedPacks = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.packs, this.columns, 'Packs');
  }

  exportExcel() {
    ToolsService.exportExcel(this.packs, this.columns, 'Packs');
  }

  exportCsv() {
    ToolsService.exportCsv(this.packs, this.columns, 'Packs');
  }

  refresh() {
    this.packsDataService.clearCache();
    this.getPacks();
  }

  packAdd(): void {
    if (!this.navigation.getParamValue('packs.addInDialog', true)) {
      this.router.navigate(['/packs/add']);
      return;
    }
    this.packAddDialog = this.dialogService.open(DialogPackFormComponent, {
      header: '', // this.translate.instant('NEWPACK'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        pack: {id: null},
        packageTypes: this.packsColumnsService.packageTypes,
        prices: this.packsColumnsService.prices,
        users: this.packsColumnsService.users,
      },
    });

    this.packAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Pack added'});
        this.getPacks();
      }
    });
  }

  packEdit(pack: Pack): void {
    if (!this.navigation.getParamValue('packs.editInDialog', true)) {
      this.router.navigate(['/packs/edit/' + pack.id]);
      return;
    }
    this.packEditDialog = this.dialogService.open(DialogPackFormComponent, {
      header: '', // this.translate.instant('EDITPACK') + ' ' + pack.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        pack: pack,
        packageTypes: this.packsColumnsService.packageTypes,
        prices: this.packsColumnsService.prices,
        users: this.packsColumnsService.users,
      },
    });

    this.packEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Pack updated'});
        this.getPacks();
      }
    });
  }

  packDetails(pack: Pack): void {
    if (!this.navigation.getParamValue('packs.detailsInDialog', true)) {
      this.router.navigate(['/packs/details/' + pack.id]);
      return;
    }
    this.packDetailsDialog = this.dialogService.open(DialogPackDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        pack: pack,
      }
    });
  }

  packDelete(pack: Pack): void {
    this.packToDelete = pack;
    this.messageService.clear();
    this.messageService.add({
      key: 'packDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + pack.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.packsDataService.deletePack(this.packToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Pack deleted'});
      this.getPacks();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Pack not deleted'});
    });
  }

  onDeleteReject(): void {
    this.packToDelete = {};
    this.messageService.clear('packDelete');
  }

}
